import React from "react";

import {
  useNavigate,
  useParams,
} from "react-router-dom";

import {
  useSelector,
} from "react-redux";

import {
  useCart,
} from "../../components/CartContext";

import "./css/CartScreen.css";


/*
=========================================================
IMAGE URL HELPER
=========================================================
*/

function getImageUrl(image) {

  if (!image) {
    return "";
  }


  if (
    image.startsWith("http://") ||
    image.startsWith("https://")
  ) {
    return image;
  }


  return `${image}`;

}


/*
=========================================================
CART SCREEN
=========================================================
*/

function CartScreen() {

  const {
    restaurantId,
    tableId,
  } = useParams();


  const navigate =
    useNavigate();


  /*
  =======================================================
  RESTAURANT INFORMATION
  =======================================================
  */

  const themeState =
    useSelector(
      (state) =>
        state.theme || {}
    );


  const restaurantName =
    themeState.restaurantName ||
    themeState.restaurant_name ||
    "CYMA GREEK TAVERNA";


  /*
  =======================================================
  CART CONTEXT
  =======================================================
  */

  const {
    cart,
    increaseQuantity,
    decreaseQuantity,
    removeFromCart,
    cartCount,
    cartTotal,
  } = useCart();


  /*
  =======================================================
  NAVIGATION
  =======================================================
  */

  const homePath =
    `/${restaurantId}/table/${tableId}`;


  const handleBackToMenu = () => {

    navigate(
      homePath
    );

  };


  const handleCheckout = () => {

    if (
      !cart ||
      cart.length === 0
    ) {
      return;
    }


    navigate(
      `${homePath}/checkout`
    );

  };


  /*
  =======================================================
  EMPTY CART
  =======================================================
  */

  if (
    !cart ||
    cart.length === 0
  ) {

    return (

      <main className="theme5-cart-page">

        <div className="theme5-cart-empty-container">


          <div className="theme5-cart-empty-label">

            YOUR CART

          </div>


          <h1>

            YOUR CART
            <br />

            IS EMPTY.

          </h1>


          <p>

            Add something delicious
            from our Greek kitchen.

          </p>


          <button

            type="button"

            className="theme5-cart-primary-button"

            onClick={
              handleBackToMenu
            }

          >

            EXPLORE THE MENU

            <span>
              →
            </span>

          </button>


        </div>

      </main>

    );

  }


  /*
  =======================================================
  CART
  =======================================================
  */

  return (

    <main className="theme5-cart-page">


      {/* =================================================
          TOP BLUE BAND
      ================================================= */}

      <section className="theme5-cart-top">

        <div className="theme5-cart-container">


          <button

            type="button"

            className="theme5-cart-back"

            onClick={
              handleBackToMenu
            }

          >

            <span>
              ←
            </span>

            BACK TO MENU

          </button>


          <div className="theme5-cart-heading">

            <span>
              {restaurantName}
            </span>

            <h1>

              YOUR CART

            </h1>

            <p>

              {cartCount}
              {" "}
              {cartCount === 1
                ? "ITEM"
                : "ITEMS"
              }

              {" "}
              READY FOR THE TABLE.

            </p>

          </div>

        </div>

      </section>


      {/* =================================================
          CART CONTENT
      ================================================= */}

      <section className="theme5-cart-content">

        <div className="theme5-cart-container">


          <div className="theme5-cart-layout">


            {/* =============================================
                ITEMS
            ============================================= */}

            <div className="theme5-cart-items">


              <div className="theme5-cart-items-heading">

                <span>
                  YOUR SELECTION
                </span>

                <span>
                  {cartCount}
                  {" "}
                  ITEMS
                </span>

              </div>


              {cart.map(
                (
                  item,
                  index
                ) => {

                  const imageUrl =
                    getImageUrl(
                      item.image ||
                      item.image_url ||
                      item.photo
                    );


                  const itemPrice =
                    Number(
                      item.price || 0
                    );


                  const itemTotal =
                    itemPrice *
                    Number(
                      item.quantity || 0
                    );


                  return (

                    <article

                      className="theme5-cart-item"

                      key={
                        item.id ||
                        `${item.name}-${index}`
                      }

                    >


                      {/* IMAGE */}

                      <div className="theme5-cart-item-image">

                        {imageUrl ? (

                          <img

                            src={imageUrl}

                            alt={
                              item.name
                            }

                          />

                        ) : (

                          <div className="theme5-cart-no-image">

                            GREEK
                            <br />
                            KITCHEN

                          </div>

                        )}

                      </div>


                      {/* INFORMATION */}

                      <div className="theme5-cart-item-info">


                        <div className="theme5-cart-item-top">

                          <span className="theme5-cart-item-number">

                            {String(
                              index + 1
                            ).padStart(
                              2,
                              "0"
                            )}

                          </span>


                          <button

                            type="button"

                            className="theme5-cart-remove"

                            onClick={() =>
                              removeFromCart(
                                item.id
                              )
                            }

                          >

                            REMOVE

                          </button>

                        </div>


                        <h2>

                          {item.name}

                        </h2>


                        {item.description && (

                          <p>

                            {item.description}

                          </p>

                        )}


                        <div className="theme5-cart-item-bottom">


                          {/* QUANTITY */}

                          <div className="theme5-cart-quantity">

                            <button

                              type="button"

                              onClick={() =>
                                decreaseQuantity(
                                  item.id
                                )
                              }

                              aria-label={
                                `Decrease ${item.name}`
                              }

                            >
                              −
                            </button>


                            <span>

                              {item.quantity}

                            </span>


                            <button

                              type="button"

                              onClick={() =>
                                increaseQuantity(
                                  item.id
                                )
                              }

                              aria-label={
                                `Increase ${item.name}`
                              }

                            >
                              +
                            </button>

                          </div>


                          {/* PRICE */}

                          <div className="theme5-cart-item-prices">

                            <span>

                              ₹
                              {itemPrice.toFixed(
                                2
                              )}

                            </span>


                            <strong>

                              ₹
                              {itemTotal.toFixed(
                                2
                              )}

                            </strong>

                          </div>

                        </div>

                      </div>

                    </article>

                  );

                }

              )}

            </div>


            {/* =============================================
                SUMMARY
            ============================================= */}

            <aside className="theme5-cart-summary">


              <div className="theme5-cart-summary-label">

                YOUR TABLE

              </div>


              <h2>

                READY
                <br />
                TO ORDER?

              </h2>


              <div className="theme5-cart-summary-table">

                <span>
                  TABLE
                </span>

                <strong>
                  {tableId || "—"}
                </strong>

              </div>


              <div className="theme5-cart-summary-line" />


              <div className="theme5-cart-summary-row">

                <span>
                  ITEMS
                </span>

                <strong>
                  {cartCount}
                </strong>

              </div>


              <div className="theme5-cart-summary-row">

                <span>
                  SUBTOTAL
                </span>

                <strong>

                  ₹
                  {Number(
                    cartTotal || 0
                  ).toFixed(2)}

                </strong>

              </div>


              <div className="theme5-cart-summary-note">

                Taxes and service charges
                will be calculated at checkout.

              </div>


              <div className="theme5-cart-summary-total">

                <span>
                  TOTAL
                </span>

                <strong>

                  ₹
                  {Number(
                    cartTotal || 0
                  ).toFixed(2)}

                </strong>

              </div>


              <button

                type="button"

                className="theme5-cart-checkout-button"

                onClick={
                  handleCheckout
                }

              >

                CONTINUE TO CHECKOUT

                <span>
                  →
                </span>

              </button>


              <button

                type="button"

                className="theme5-cart-continue-button"

                onClick={
                  handleBackToMenu
                }

              >

                + ADD MORE TO CART

              </button>


              <div className="theme5-cart-security">

                FRESHLY PREPARED
                <span>
                  •
                </span>
                MADE TO SHARE

              </div>

            </aside>

          </div>


          {/* =================================================
              BOTTOM MESSAGE
          ================================================= */}

          <div className="theme5-cart-bottom-message">

            <span>
              OPA!
            </span>

            <p>

              Good food is meant to be
              shared around the table.

            </p>

          </div>

        </div>

      </section>

    </main>

  );

}


export default CartScreen;